import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["fieldValue", "inputActionName"],
  _excluded2 = ["fieldValue"];
import EnrollmentDatasetPropertyField from 'automation-ui-action-features/actions/extension/generic-extension/EnrollmentDatasetPropertyField';
import EnrollmentEventPropertyField from 'automation-ui-action-features/actions/extension/generic-extension/EnrollmentEventPropertyField';
import FetchedObjectPropertyField from 'automation-ui-action-features/actions/extension/generic-extension/FetchedObjectPropertyField';
import FieldDataField from 'automation-ui-action-features/actions/extension/generic-extension/FieldDataField';
import MultiDataField from 'automation-ui-action-features/actions/extension/generic-extension/MultiDataField';
import PropertyField from 'automation-ui-action-features/actions/extension/generic-extension/PropertyField';
import RelativeDatetimeField from 'automation-ui-action-features/actions/extension/generic-extension/RelativeDatetimeField';
import SingleObjectCoordinatesField from 'automation-ui-action-features/actions/extension/generic-extension/SingleObjectCoordinatesField';
import StaticField from 'automation-ui-action-features/actions/extension/generic-extension/StaticField';
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
import setIn from 'immutable-less/transmute/setIn';
/**
 * Inverse of jsonFieldValue.
 */
const instantiateFieldValue = fieldValue => {
  if (!fieldValue) return null;
  switch (fieldValue.valueType) {
    case 'ENROLLMENT_DATASET_COLUMN':
      return new EnrollmentDatasetPropertyField(fieldValue);
    case 'ENROLLMENT_EVENT_PROPERTY':
      return new EnrollmentEventPropertyField(fieldValue);
    case 'FETCHED_OBJECT_PROPERTY':
      return new FetchedObjectPropertyField(fieldValue);
    case 'FIELD_DATA':
      return new FieldDataField(fieldValue);
    case 'MULTI':
      return new MultiDataField(fieldValue);
    case 'PROPERTY':
      return new PropertyField(fieldValue);
    case 'RELATIVE_DATETIME':
      return new RelativeDatetimeField(fieldValue);
    case 'SINGLE_OBJECT_COORDINATES':
      return new SingleObjectCoordinatesField(fieldValue);
    case 'EXTERNAL': // remove this case after migration
    case 'STATIC': // remove this case after migration
    case 'SINGLE':
      return new StaticField(fieldValue);
    default:
      return null;
  }
};

/**
 * Inverse of instantiateFieldValue.
 *
 * All records should have a toJSON method, but TS is complaining that the
 * signatures are not compatible with one another, which is technically true.
 * Since we _care_ about type safety, we're going to maintain this function,
 * even though it's 100% redundant.
 */
const jsonFieldValue = fieldValue => {
  if (!fieldValue) return null;
  switch (fieldValue.valueType) {
    case 'ENROLLMENT_DATASET_COLUMN':
      return fieldValue.toJSON();
    case 'ENROLLMENT_EVENT_PROPERTY':
      return fieldValue.toJSON();
    case 'FETCHED_OBJECT_PROPERTY':
      return fieldValue.toJSON();
    case 'FIELD_DATA':
      return fieldValue.toJSON();
    case 'MULTI':
      return fieldValue.toJSON();
    case 'PROPERTY':
      return fieldValue.toJSON();
    case 'RELATIVE_DATETIME':
      return fieldValue.toJSON();
    case 'SINGLE_OBJECT_COORDINATES':
      return fieldValue.toJSON();
    case 'EXTERNAL': // remove this case after migration
    case 'STATIC': // remove this case after migration
    case 'SINGLE':
      return fieldValue.toJSON();
    default:
      return null;
  }
};
export default class GenericActionField extends createImmutableObjectClass({
  fieldKey: null,
  fieldValue: null,
  inputActionName: null
}, {
  fromJSON: _ref => {
    let {
        fieldValue,
        inputActionName
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({}, rest, {
      fieldValue: instantiateFieldValue(fieldValue),
      inputActionName: inputActionName || null
    });
  },
  toJSON: _ref2 => {
    let {
        fieldValue
      } = _ref2,
      rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
    return Object.assign({}, rest, {
      fieldValue: jsonFieldValue(fieldValue)
    });
  }
}) {
  getFieldValueProperty() {
    const {
      fieldValue
    } = this;
    if (fieldValue instanceof EnrollmentDatasetPropertyField || fieldValue instanceof EnrollmentEventPropertyField || fieldValue instanceof FetchedObjectPropertyField) {
      return 'propertyToken';
    }
    if (fieldValue instanceof FieldDataField) {
      return 'fieldDataIdentifier';
    }
    if (fieldValue instanceof PropertyField) {
      return 'propertyName';
    }
    if (fieldValue instanceof RelativeDatetimeField) {
      return 'timeDelay';
    }
    if (fieldValue instanceof SingleObjectCoordinatesField) {
      return 'coordinatesToken';
    }
    return 'value';
  }
  getIsFieldValueEmpty() {
    return !this.fieldValue || this.fieldValue.getIsValueEmpty();
  }
  setFieldValue(fieldValue) {
    return this.set('fieldValue', instantiateFieldValue(fieldValue));
  }
  setFieldValueValue(value) {
    return setIn(['fieldValue', this.getFieldValueProperty()], value, this);
  }
}